// apps/web/src/lib/badges/badgeStore.ts
// ─────────────────────────────────────────────────────────────────────────
// Durable store of EARNED badges (localStorage, V1; API-ready shape).
//
// One record per earned badge: { badgeId, earnedAt, retroactive }. Earning is
// permanent and idempotent — re-earning never overwrites the original
// earnedAt. The slice is the reactive mirror; this module owns the bytes.
// ─────────────────────────────────────────────────────────────────────────

import type { EarnedBadge } from '@nisk/core/badges';
import { isKnownBadge } from '@nisk/core/badges';

const EARNED_KEY = 'nis.badges.earned';

export function readEarnedBadges(): Record<string, EarnedBadge> {
	try {
		const raw = localStorage.getItem(EARNED_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw) as Record<string, EarnedBadge>;
		// Drop any unknown ids defensively (renamed/removed badges).
		const clean: Record<string, EarnedBadge> = {};
		for (const [id, rec] of Object.entries(parsed)) {
			if (isKnownBadge(id) && rec?.badgeId) clean[id] = rec;
		}
		return clean;
	} catch {
		return {};
	}
}

function writeEarnedBadges(map: Record<string, EarnedBadge>): void {
	try {
		localStorage.setItem(EARNED_KEY, JSON.stringify(map));
	} catch {
		/* noop */
	}
}

/**
 * Persist a set of newly-earned ids. Existing earned records are preserved
 * (earnedAt never changes). Returns the records that were ACTUALLY new this
 * call, so the caller can celebrate only fresh badges.
 */
export function persistNewlyEarned(
	ids: string[],
	opts: { retroactive: boolean },
): EarnedBadge[] {
	const map = readEarnedBadges();
	const now = new Date().toISOString();
	const fresh: EarnedBadge[] = [];

	for (const id of ids) {
		if (!isKnownBadge(id)) continue;
		if (map[id]) continue; // already earned — keep original
		const rec: EarnedBadge = {
			badgeId: id,
			earnedAt: now,
			retroactive: opts.retroactive,
		};
		map[id] = rec;
		fresh.push(rec);
	}

	if (fresh.length) writeEarnedBadges(map);
	return fresh;
}
